import type * as DomainValues from '@/generated/core-enums'
import type { components } from '@/generated/core-api'
import { beginSignInReminder, clearSignInReminder } from '@/shared/session/signInReminder'
import { isDirection, isLocale, type Locale } from '@/shared/i18n/locale'
import type {
  AccountContext,
  ClinicSettings,
  Identity,
  AccountSession,
  SessionChannel,
  UpdateClinicSettingsInput,
} from '@/shared/session/types'
import {
  isClinicDateFormat,
  isClinicNumberFormat,
  isClinicTimeFormat,
  isDiagnosisTerminology,
  isReleaseMode,
} from '@/shared/session/types'

import { CoreError } from './errors'
import { browserSessionContext, notifySessionContextChange } from './sessionContext'
import { API_ROOT, asString, request } from './transport'

/**
 * Session and application context. Wire types are generated from
 * `contracts/public-api/core/openapi.yaml` into `src/generated/core-api.ts` —
 * regenerate with `npm run generate:core-client`, never hand-edit.
 *
 * This module and `patientsClient.ts` are the only places in `web/` that name a
 * wire field. Everything above them consumes the domain types in
 * `shared/session/types.ts`.
 */

type WireLoginResponse = components['schemas']['LoginResponse']
type WireAppContext = components['schemas']['AppContextResponse']
type WireSessionSnapshot = components['schemas']['SessionSnapshotResponse']
type WireTelegramMiniAppLoginRequest = components['schemas']['TelegramMiniAppLoginRequest']
type WireUpdateAccountLanguageRequest = components['schemas']['UpdateAccountLanguageRequest']
type WireClinicSettings = components['schemas']['ClinicSettings']
type WireUpdateClinicSettingsRequest = components['schemas']['UpdateClinicSettingsRequest']
type WireSwitchClinicRequest = components['schemas']['SwitchClinicRequest']
type WireDeveloperElevationRequest = components['schemas']['DeveloperElevationRequest']
type WireOrganizationCreationOptions = components['schemas']['OrganizationCreationOptions']
type WireCreateOrganizationRequest = components['schemas']['CreateOrganizationRequest']
type WireCreateOrganizationResponse = components['schemas']['CreateOrganizationResponse']

export interface LoginInput {
  username: string
  password: string
}

export interface OrganizationCreationOptions {
  canCreate: boolean
  calendars: Array<{ countryCode: string; subdivisionCode: string | null; displayName: string; year: number }>
}

export interface CreateOrganizationInput {
  idempotencyKey: string
  name: string
  timeZone: string
  calendarCountryCode: string
  calendarSubdivisionCode: string | null
  language: Locale
}

/** Authenticate and let the server set its HttpOnly cookie. Returns the user. */
export async function login(input: LoginInput): Promise<Identity> {
  const body = await request('POST', `${API_ROOT}/auth/login`, {
    body: input,
    // A 400 here is bounded input validation on credentials we already
    // client-side validated; to the person signing in it is still a failed
    // attempt, and it must stay indistinguishable from a wrong password.
    statusKinds: {
      400: 'invalid-credentials',
      401: 'invalid-credentials',
      429: 'throttled',
    },
  })
  const identity = readIdentity(body)
  browserSessionContext.startLogin()
  notifySessionContextChange()
  beginSignInReminder(identity.id)
  return identity
}

/**
 * Exchange a staff Telegram Mini App launch for the same HttpOnly session
 * cookie `login` sets. The server verifies the launch against the staff bot
 * and admits only a Telegram user already linked to an active account; nothing
 * is created for an unknown sender.
 *
 * Every refusal keeps its machine code on the error (`coreErrorCode`): a
 * `telegram_mini_app_not_linked` launch needs the person to link Telegram on
 * the desktop first, `telegram_mini_app_launch_expired` wants a fresh launch,
 * and `telegram_mini_app_not_configured` means the clinic has no staff bot.
 */
export async function loginWithTelegramMiniApp(initData: string): Promise<Identity> {
  const body = { initData } satisfies WireTelegramMiniAppLoginRequest
  const response = await request('POST', `${API_ROOT}/auth/telegram-mini-app`, {
    body,
    statusKinds: {
      // A malformed or oversized launch payload never came from Telegram's
      // launcher; the bridge treats it as a refused launch, not a form error.
      400: 'invalid-credentials',
      401: 'invalid-credentials',
      // No enabled staff bot: the Mini App cannot sign anyone in until the
      // clinic configures one, which is an unavailability, not a conflict.
      409: 'unavailable',
      429: 'throttled',
    },
  })
  const identity = readIdentity(response)
  browserSessionContext.startLogin()
  notifySessionContextChange()
  beginSignInReminder(identity.id)
  return identity
}

export async function logout(): Promise<void> {
  // Logout is idempotent server-side. An already-dead session still satisfies
  // the caller's intent, so it must not surface as an error.
  await request('POST', `${API_ROOT}/auth/logout`, { ignoreStatuses: [401] })
  clearSignInReminder()
}

export async function getMe(): Promise<Identity> {
  return readIdentity(await request('GET', `${API_ROOT}/auth/me`))
}

export async function getAppContext(): Promise<AccountContext> {
  return readAppContext(await request('GET', `${API_ROOT}/app-context`))
}

export async function getSessionSnapshot(reconcile = false): Promise<AccountSession> {
  const body = await request('GET', `${API_ROOT}/auth/session`)
  const wire = body as WireSessionSnapshot | undefined
  if (!wire?.user || !wire.context) {
    throw new CoreError('malformed', 'session response is missing identity or context')
  }
  const context = readAppContext(wire.context)
  if (!reconcile) browserSessionContext.install(context.sessionContext, false)
  return {
    identity: readIdentity({ user: wire.user }),
    context,
    channel: readSessionChannel(wire.channel),
  }
}

/**
 * The contract marks `channel` optional so older snapshots stay valid, but the
 * server always sends it. An absent or unknown value is read as `password`:
 * the conservative answer, since a session that cannot say it was made from a
 * launch is treated as one that was not. The Mini App gate exchanges every
 * launch is exchanged by Doctor Mini App; the
 * channel is what the product and a support conversation read afterwards.
 */
function readSessionChannel(value: unknown): SessionChannel {
  return value === 'telegram_mini_app' || value === 'telegram_login' ? value : 'password'
}

export async function updateAccountLanguage(language: Locale): Promise<AccountContext> {
  const body = { language } satisfies WireUpdateAccountLanguageRequest
  const context = readAppContext(await request('PUT', `${API_ROOT}/account/language`, { body }))
  browserSessionContext.install(context.sessionContext, false)
  return context
}

export async function switchActiveClinic(clinicId: string): Promise<AccountContext> {
  const body = { clinicId } satisfies WireSwitchClinicRequest
  const context = readAppContext(await request('PUT', `${API_ROOT}/auth/clinic`, { body }))
  notifySessionContextChange()
  return context
}

export async function getOrganizationCreationOptions(): Promise<OrganizationCreationOptions> {
  return readOrganizationCreationOptions(await request('GET', `${API_ROOT}/organizations/creation-options`))
}

export async function createOrganization(input: CreateOrganizationInput): Promise<{ context: AccountContext; replayed: boolean }> {
  const body = input satisfies WireCreateOrganizationRequest
  const wire = await request('POST', `${API_ROOT}/organizations`, { body }) as WireCreateOrganizationResponse | undefined
  if (!wire || typeof wire.replayed !== 'boolean') {
    throw new CoreError('malformed', 'organization creation response is incomplete')
  }
  const context = readAppContext(wire.context)
  browserSessionContext.install(context.sessionContext, false)
  notifySessionContextChange()
  return { context, replayed: wire.replayed }
}

export async function activateDeveloperElevation(input: {
  mode: DomainValues.DeveloperElevationMode
  reason: string
}): Promise<AccountContext> {
  const body = input satisfies WireDeveloperElevationRequest
  return readAppContext(await request('POST', `${API_ROOT}/developer/elevation`, { body }))
}

export async function deactivateDeveloperElevation(): Promise<AccountContext> {
  return readAppContext(await request('DELETE', `${API_ROOT}/developer/elevation`))
}

export async function getClinicSettings(): Promise<ClinicSettings> {
  return readClinicSettings(await request('GET', `${API_ROOT}/clinic/settings`))
}

export async function updateClinicSettings(
  input: UpdateClinicSettingsInput,
): Promise<ClinicSettings> {
  const body = {
    revision: input.revision,
    timeZone: input.timeZone,
    calendarCountryCode: input.calendarCountryCode,
    calendarSubdivisionCode: input.calendarSubdivisionCode,
    dateFormat: input.dateFormat,
    timeFormat: input.timeFormat,
    numberFormat: input.numberFormat,
  } satisfies WireUpdateClinicSettingsRequest
  return readClinicSettings(await request('PUT', `${API_ROOT}/clinic/settings`, { body }))
}

function readIdentity(body: unknown): Identity {
  const user = (body as WireLoginResponse | undefined)?.user
  const account = user?.account
  if (!account || !asString(account.id) || !asString(account.displayName) ||
      (account.username !== null && !asString(account.username)) ||
      (account.personId !== null && !asString(account.personId)) || typeof user?.isClinicOwner !== 'boolean' ||
      typeof user.designAccess !== 'boolean') {
    throw new CoreError('malformed', 'auth response did not carry a user account')
  }
  return {
    id: account.id,
    username: account.username,
    displayName: account.displayName,
    personId: account.personId,
    accountKind: account.accountKind,
    passwordChangeRequired: account.passwordChangeRequired,
    roles: readStringList(user?.roles),
    capabilities: readStringList(user?.capabilities),
    isClinicOwner: user.isClinicOwner,
    designAccess: user.designAccess,
  }
}

function readOrganizationCreationOptions(body: unknown): OrganizationCreationOptions {
  const wire = body as WireOrganizationCreationOptions | undefined
  if (!wire || typeof wire.canCreate !== 'boolean' || !Array.isArray(wire.calendars)) {
    throw new CoreError('malformed', 'organization creation options are incomplete')
  }
  return {
    canCreate: wire.canCreate,
    calendars: wire.calendars.map((calendar) => {
      if (!asString(calendar.countryCode) || (calendar.subdivisionCode !== null && !asString(calendar.subdivisionCode)) ||
          !asString(calendar.displayName) || !Number.isInteger(calendar.year)) {
        throw new CoreError('malformed', 'organization calendar option is incomplete')
      }
      return {
        countryCode: calendar.countryCode,
        subdivisionCode: calendar.subdivisionCode,
        displayName: calendar.displayName,
        year: calendar.year,
      }
    }),
  }
}

function readAppContext(body: unknown): AccountContext {
  const wire = body as WireAppContext | undefined
  const clinic = wire?.clinic
  const practitioner = wire?.practitioner ?? null
  const locale = wire?.locale
  const availableClinics = wire?.availableClinics
  const elevation = wire?.elevation
  const releaseMode = wire?.releaseMode
  const sessionContext = wire?.sessionContext

  // These strings are what the header actually renders. Refusing an incomplete
  // context is what keeps a degraded API from producing a shell that looks
  // signed in with a blank identity.
  if (clinic === undefined || !locale || !Array.isArray(availableClinics) || elevation === undefined || !isReleaseMode(releaseMode) ||
      !sessionContext || !asString(sessionContext.sessionId) || !Number.isSafeInteger(sessionContext.revision) || sessionContext.revision < 1 || sessionContext.clinicId !== (clinic?.id ?? '')) {
    throw new CoreError('malformed', 'app-context is missing required authority fields')
  }
  if (clinic !== null && (
    !clinic.operatingEntity || !asString(clinic.operatingEntity.id) || !asString(clinic.operatingEntity.versionId) ||
    !asString(clinic.operatingEntity.displayName) || !asString(clinic.operatingEntity.country) || !Number.isInteger(clinic.operatingEntity.revision) || clinic.operatingEntity.revision < 1 ||
    !asString(clinic.timeZone) ||
    !asString(clinic.calendarCountryCode) ||
    (clinic.calendarSubdivisionCode !== null && !asString(clinic.calendarSubdivisionCode)) ||
    !asString(clinic.id) ||
    !isClinicDateFormat(clinic.dateFormat) ||
    !isClinicTimeFormat(clinic.timeFormat) ||
    !isClinicNumberFormat(clinic.numberFormat) ||
    (clinic.diagnosisTerminology !== null && !isDiagnosisTerminology(clinic.diagnosisTerminology))
  )) {
    throw new CoreError('malformed', 'app-context clinic is incomplete')
  }
  if (practitioner !== null && (!asString(practitioner.displayName) || !asString(practitioner.id))) {
    throw new CoreError('malformed', 'app-context practitioner is incomplete')
  }
  if (clinic === null && (practitioner !== null || elevation !== null || readStringList(wire?.capabilities).length !== 0)) {
    throw new CoreError('malformed', 'account-only context carried organization authority')
  }
  if (!isLocale(locale.language)) {
    throw new CoreError('malformed', 'app-context locale is not a supported language')
  }
  if (!isDirection(locale.direction)) {
    throw new CoreError('malformed', 'app-context locale direction is not supported')
  }
  if (
    elevation !== null &&
    (!asString(elevation.id) ||
      !asString(elevation.clinicId) ||
      !asString(elevation.reason) ||
      !asString(elevation.activatedAt) ||
      (elevation.mode !== 'temporary' && elevation.mode !== 'development_persistent') ||
      (elevation.mode === 'temporary' && !asString(elevation.expiresAt)) ||
      (elevation.mode === 'development_persistent' && elevation.expiresAt !== null))
  ) {
    throw new CoreError('malformed', 'app-context developer elevation is incomplete')
  }

  return {
    sessionContext,
    clinic: clinic === null ? null : {
      id: clinic.id,
      operatingEntity: clinic.operatingEntity,
      timeZone: clinic.timeZone,
      calendarCountryCode: clinic.calendarCountryCode,
      calendarSubdivisionCode: clinic.calendarSubdivisionCode,
      dateFormat: clinic.dateFormat,
      timeFormat: clinic.timeFormat,
      numberFormat: clinic.numberFormat,
      diagnosisTerminology: clinic.diagnosisTerminology,
    },
    practitioner:
      practitioner === null
        ? null
        : {
            id: practitioner.id,
            displayName: practitioner.displayName,
          },
    language: locale.language,
    direction: locale.direction,
    capabilities: readStringList(wire?.capabilities),
    releaseMode,
    availableClinics: availableClinics.map((membership) => {
      if (!asString(membership.id) || !asString(membership.name) || !asString(membership.country) || !asString(membership.timeZone)) {
        throw new CoreError('malformed', 'app-context clinic membership is incomplete')
      }
      return membership
    }),
    elevation: elevation === null
      ? null
      : {
          id: elevation.id,
          clinicId: elevation.clinicId,
          mode: elevation.mode,
          reason: elevation.reason,
          activatedAt: elevation.activatedAt,
          expiresAt: elevation.expiresAt,
        },
  }
}

function readClinicSettings(body: unknown): ClinicSettings {
  const settings = body as WireClinicSettings | undefined
  if (
    !settings ||
    !asString(settings.id) ||
    !asString(settings.timeZone) ||
    !asString(settings.calendarCountryCode) ||
    (settings.calendarSubdivisionCode !== null && !asString(settings.calendarSubdivisionCode)) ||
    !isClinicDateFormat(settings.dateFormat) ||
    !isClinicTimeFormat(settings.timeFormat) ||
    !isClinicNumberFormat(settings.numberFormat) ||
    (settings.diagnosisTerminology !== null && !isDiagnosisTerminology(settings.diagnosisTerminology)) ||
    !Number.isInteger(settings.revision) ||
    settings.revision < 1 ||
    !asString(settings.updatedAt)
  ) {
    throw new CoreError('malformed', 'clinic settings response is incomplete')
  }
  return {
    id: settings.id,
    timeZone: settings.timeZone,
    calendarCountryCode: settings.calendarCountryCode,
    calendarSubdivisionCode: settings.calendarSubdivisionCode,
    dateFormat: settings.dateFormat,
    timeFormat: settings.timeFormat,
    numberFormat: settings.numberFormat,
    diagnosisTerminology: settings.diagnosisTerminology,
    revision: settings.revision,
    updatedAt: settings.updatedAt,
  }
}

function readStringList(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}
